import { Box, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import {
    PoolActionLabel,
    PoolCard,
    PoolCardActionContainer,
    PoolCardContent,
    PoolCardHeader,
    PoolCardProps,
    PoolLineClampTypography,
    PoolTypography,
} from '@synergy/components-next';
import { ReactElement, useState } from 'react';

import { CardImage } from './components/card-image';
import { ROUTES } from '../../../../utils/routes';
import { SupplierSpecialOffer } from '../../../../models/supplier-special-offer';

const StyledPoolCardHeader = styled(PoolCardHeader, { shouldForwardProp: prop => prop !== 'hasSpecialOfferImage' })<{
    hasSpecialOfferImage: boolean;
}>(({ theme, hasSpecialOfferImage }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'end',
    height: theme.spacing(24),
    paddingTop: theme.spacing(3),

    ...(hasSpecialOfferImage && {
        padding: 0,
    }),
}));

const StyledPoolCardContent = styled(PoolCardContent)(({ theme }) => ({
    height: '100%',
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
}));

const ActionText = styled(PoolTypography, { shouldForwardProp: prop => prop !== 'hover' })<{ hover: boolean }>(
    ({ theme, hover }) => ({
        alignSelf: 'end',
        paddingBottom: theme.spacing(2),
        transition: 'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

        [theme.breakpoints.up('sm')]: {
            color: hover ? theme.palette.primary.main : undefined,
        },
    })
);

export interface SupplierCardProps extends Omit<PoolCardProps, 'onClick'> {
    specialOffer: SupplierSpecialOffer;
    onClick?: (specialOffer: SupplierSpecialOffer) => void;
}

/**
 * SupplierCard<br>
 * <a href="https://www.figma.com/file/UXG8mmJQdrAX2oDRp0FTBl/Pool-Synergy-Shop?node-id=4091%3A286460" target="_blank">Open in Figma</a>
 */
export const SupplierOfferCard = (props: SupplierCardProps): ReactElement => {
    const { specialOffer, onClick, ...cardProps } = props;
    const [hover, setHover] = useState(false);
    const [imageError, setImageError] = useState(false);
    const searchParams = new URLSearchParams({
        [FilterParams.SupplierIds]: specialOffer.supplier.id.toString(),
        [FilterParams.SpecialPrice]: 'true',
    });

    return (
        <PoolCard
            {...cardProps}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            onClick={() => onClick?.(specialOffer)}
            href={ROUTES.view.replace(':slug', specialOffer.path + '?' + searchParams.toString())}
            sx={{ height: 376 }}
        >
            <StyledPoolCardHeader hasSpecialOfferImage={!!specialOffer.image}>
                <PoolCardActionContainer>
                    <PoolActionLabel color="dark">
                        <FormattedMessage
                            id="app.specialOffer.label"
                            values={{
                                date: <FormattedDate value={specialOffer.validTo} dateStyle="short" />,
                            }}
                        />
                    </PoolActionLabel>
                </PoolCardActionContainer>
                <CardImage
                    specialOffer={specialOffer}
                    imageError={imageError}
                    onError={hasError => setImageError(hasError)}
                />
            </StyledPoolCardHeader>

            <StyledPoolCardContent>
                <Box>
                    <PoolTypography variant="subtitle1" component="div">
                        {specialOffer.title}
                    </PoolTypography>

                    <PoolLineClampTypography maxLines={3} variant="body1">
                        {specialOffer.subtitle}
                    </PoolLineClampTypography>
                </Box>
                <ActionText variant="button" hover={hover} translationId="app.supplierOffer.buyNow" />
            </StyledPoolCardContent>
        </PoolCard>
    );
};
